import React from 'react';
import { useTranslation } from 'react-i18next';
import { ApolloError } from '@apollo/client/errors';
import _get from 'lodash/get';
import {
  FormTemplateQuestionFieldOptionsValidityHintEnum,
  TaskScopeNameEnum,
} from 'graphql-common';
import CountUp from '@lib/components/CountUp/CountUp';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import Typography from '@lib/components/Typography/Typography';
import Trans from '@lib/components/Trans/Trans';
import IconSurface from '@lib/components/IconSurface/IconSurface';
import { Colors } from '@lib/enums/colors';
import CountMark, { CountMarkTypes } from '@lib/components/CountMark/CountMark';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import { TaskCounter } from '@lib/interfaces/task';
import { Values } from '@lib/interfaces/form';
import TasksFilterFieldNames from '@lib/enums/fieldNames/tasksFilterFieldNames';
import { SelectOption } from '@lib/components/Select/types';
import styles from './TasksTimelineStateCounters.module.scss';

const getCounters = ({
  timelineAll = 0,
  timelineFailure = 0,
  timelineWarning = 0,
}: TaskCounter) => [
  {
    type: TaskScopeNameEnum.Success,
    title: 'completions',
    count: timelineAll,
    icon: 'verified',
    color: Colors.Lime,
    symbolsOutlined: false,
  },
  {
    type: TaskScopeNameEnum.Warning,
    title: 'warnings',
    count: timelineWarning,
    subCount:
      timelineWarning && timelineAll
        ? (timelineWarning * 100) / timelineAll
        : undefined,
    icon: 'warning',
    color: Colors.Orange,
    symbolsOutlined: false,
  },
  {
    type: TaskScopeNameEnum.Failure,
    title: 'alerts',
    count: timelineFailure,
    subCount:
      timelineFailure && timelineAll
        ? (timelineFailure * 100) / timelineAll
        : undefined,
    icon: 'error',
    color: Colors.Red,
    symbolsOutlined: false,
  },
];

type TaskResultStateCountersProps = {
  taskCounterData: TaskCounter;
  tasksFirstLoading: boolean;
  tasksError?: ApolloError;
  filterParams: Values;
};

export default function TasksTimelineStateCounters(
  props: TaskResultStateCountersProps,
) {
  const { taskCounterData, tasksFirstLoading, tasksError, filterParams } =
    props;
  const { t } = useTranslation();

  // TODO fix it
  const newTaskCounterData = { ...taskCounterData };
  const validityHints = (
    _get(
      filterParams,
      [TasksFilterFieldNames.ValidityHints],
      [],
    ) as SelectOption[]
  ).map(({ value }) => value);
  const hasSuccess = validityHints.includes(
    FormTemplateQuestionFieldOptionsValidityHintEnum.Success,
  );
  const hasFailure = validityHints.includes(
    FormTemplateQuestionFieldOptionsValidityHintEnum.Failure,
  );
  const hasWarning = validityHints.includes(
    FormTemplateQuestionFieldOptionsValidityHintEnum.Warning,
  );
  if ((hasSuccess || hasFailure) && !hasWarning) {
    newTaskCounterData.timelineWarning = undefined;
  }
  if ((hasSuccess || hasWarning) && !hasFailure) {
    newTaskCounterData.timelineFailure = undefined;
  }

  if (tasksError) return null;
  if (tasksFirstLoading) return null;
  const counters = getCounters(newTaskCounterData);
  return (
    <PageBody className={styles.countersPageBody}>
      <div className={styles.counters}>
        {counters.map(
          ({
            type,
            title,
            count,
            icon,
            color,
            subCount,
            symbolsOutlined = false,
          }) => (
            <div
              key={type}
              data-test-type={type}
              className={styles.counterItem}
            >
              <IconSurface
                icon={icon}
                color={color}
                size="lg"
                symbolsOutlined={symbolsOutlined}
                surfaceClassName={styles.counterItemIconCircle}
                iconClassName={styles.counterItemIcon}
              />
              <div>
                <Typography variant="caption" strong className={styles.title}>
                  <Trans i18nKey={title} />
                </Typography>
                <div className={styles.countersRow}>
                  <Typography variant="h2" className={styles.counter}>
                    <CountUp end={count} duration={3} />
                  </Typography>
                  <Tooltip body={t('solved')}>
                    <div className={styles.solvedCounter}>
                      <CountMark
                        value={subCount ? Math.round(subCount) : 0}
                        type={CountMarkTypes.blue}
                        disabled
                        useCountUp
                        postfix="%"
                      />
                    </div>
                  </Tooltip>
                </div>
              </div>
            </div>
          ),
        )}
      </div>
    </PageBody>
  );
}
